import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CreditCard, ArrowRightCircle, Mail, Phone, FileText, Landmark } from "lucide-react";
import { CreditCardForm } from "../components/payment/CreditCardForm";
import { ProcessingScreen } from "../components/payment/ProcessingScreen";
import { ResultScreen } from "../components/payment/ResultScreen";
import { usePayment } from "../hooks/usePayment";
import { applyPassportService } from '../services/passportApi';
import Header from "../components/Public/ui/Header";
import Footer from "../components/Public/ui/Footer";

function PaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Data recovery
  const stepData = location.state?.data ?? location.state?.applicationData ?? JSON.parse(sessionStorage.getItem("passport:step1") || "null");
  const userData = location.state?.userData ?? null;
  const files = location.state?.files || {};
  const applicationNumber = location.state?.applicationNumber ?? stepData?.application_number ?? "PENDING";

  const [currentState, setCurrentState] = useState("payment-form");

  const initialAmount = useMemo(() => {
    const serviceType = stepData?.serviceType ?? stepData?.service_type ?? "Normal";
    return serviceType === "Urgent" ? 15000 : 10000;
  }, [stepData]);

  const { currentTransaction, isProcessing, initializePayment, processPayment } = usePayment();

  useEffect(() => {
    initializePayment({
      amount: initialAmount,
      currency: "LKR",
      merchantName: "Dept. of Immigration & Emigration",
      orderId: `PAY-${Date.now()}`,
    });
  }, [initialAmount, initializePayment]);

  const handlePaymentSubmit = async (paymentData) => {
    setCurrentState("processing");

    // Properly call the processPayment hook so currentTransaction status updates
    const result = await processPayment("card", paymentData);

    try {
      if (applicationNumber && applicationNumber !== "PENDING") {
        // CASE: Existing application - Update payment status only
        console.log(`[PaymentPage] Updating existing application: ${applicationNumber}`);
        await applyPassportService.confirmPayment(applicationNumber, {
          payment_status: result.success ? 'success' : 'failed',
          payment_reference: result.transactionId || currentTransaction?.id,
          amount: initialAmount,
          method: 'card',
          transaction_id: result.transactionId || currentTransaction?.id,
          paid_at: new Date().toISOString()
        });
      } else {
        // CASE: New application - Submit full record
        console.log("[PaymentPage] Submitting new application record");
        const submissionData = {
          ...stepData,
          paymentStatus: result.success ? "success" : "failed",
          paymentReference: result.transactionId || currentTransaction?.id,
        };
        await applyPassportService.submitApplication(submissionData, files);
      }
    } catch (error) {
      console.error("[PaymentPage] Post-payment API sync failed:", error);
      // Even if sync fails, we move to result screen (which will show the transaction status)
    } finally {
      setCurrentState("result");
    }
  };

  const handleReturnToMerchant = () => {
    navigate('/passport/status/pending', {
      state: {
        transactionId: currentTransaction?.id || `TXN-${Date.now()}`,
        applicationNumber: applicationNumber,
        amount: initialAmount,
        applicationData: stepData,
        userData: userData
      }
    });
  };

  if (!currentTransaction) return null;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Header />

      <main className="flex-grow flex items-center justify-center p-4"
        style={{
          backgroundImage: "radial-gradient(circle at center, rgba(255,255,255,0.9) 0%, rgba(241,245,249,1) 100%), url('/assets/Images/SLUDI-background.png')",
          backgroundSize: "cover",
          backgroundAttachment: "fixed"
        }}>

        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

          {/* Theme Consistent Header */}
          <div className="bg-blue-50/50 border-b border-blue-100 p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-600 p-2.5 rounded-xl shadow-md">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900 leading-tight">Complete payment</h2>
                <p className="text-slate-500 text-xs mt-0.5">Please enter your card details to finalize the application.</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-6">

            {/* 1. Integrated Order Summary (Consistent Pattern) */}
            <section className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <FileText size={12} /> Transaction summary
              </h3>
              <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                <SummaryItem label="Ref number" value={applicationNumber} isMono />
                <SummaryItem label="Service" value={stepData?.service_type || "Standard"} />
                <div className="col-span-2 pt-2 border-t border-slate-200 mt-1 flex justify-between items-end">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Total amount</span>
                  <span className="text-xl font-black text-blue-600">LKR {initialAmount.toLocaleString()}.00</span>
                </div>
              </div>
            </section>

            {/* 2. Payment Form Area */}
            <div className="min-h-[280px]">
              {currentState === "payment-form" && (
                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <CreditCardForm onSubmit={handlePaymentSubmit} isProcessing={isProcessing} />

                  <div className="mt-6 flex items-center justify-center gap-4 opacity-40 grayscale scale-75">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/9/98/Visa_Inc._logo_%282005%E2%80%932014%29.svg" alt="Visa" className="h-4" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6" />
                  </div>
                </div>
              )}

              {currentState === "processing" && (
                <div className="py-12">
                  <ProcessingScreen transaction={currentTransaction} />
                </div>
              )}

              {currentState === "result" && (
                <ResultScreen 
                  transaction={currentTransaction} 
                  applicationNumber={applicationNumber}
                  applicationData={stepData}
                  userData={userData}
                  onReturnToMerchant={handleReturnToMerchant} 
                />
              )}
            </div>

            {/* 3. Theme Consistent Help Footer */}
            <footer className="pt-2">
              <div className="flex flex-col items-center text-center gap-3 p-4 bg-slate-900 rounded-xl text-white">
                <div>
                  <h4 className="text-xs font-bold">Payment Helpdesk</h4>
                  <p className="text-slate-400 text-[10px]">For billing inquiries, please contact our support team.</p>
                </div>
                <div className="flex gap-2 w-full">
                  <a href="mailto:passport.support@gov.lk" className="flex-1 flex items-center justify-center gap-2 py-2 bg-slate-800 rounded-lg text-[10px] border border-slate-700">
                    <Mail size={12} className="text-blue-400" /> Email
                  </a>
                  <a href="tel:+94112345678" className="flex-1 flex items-center justify-center gap-2 py-2 bg-blue-600 rounded-lg text-[10px] font-bold">
                    <Phone size={12} /> +94 11 234 5678
                  </a>
                </div>
              </div>
            </footer>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

/* Local Patterns to match Status pages */
function SummaryItem({ label, value, isMono = false }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tight">{label}</span>
      <span className={`text-slate-800 font-bold leading-tight ${isMono ? 'font-mono text-xs' : 'text-sm'}`}>
        {value || '—'}
      </span>
    </div>
  );
}

export default PaymentPage;